// src/App.js
import React, { useEffect, useState, useMemo } from "react";
import "./pages/HomeDashboard.css";
import logo from "./logo.png";
import LoginPage from "./LoginPage";

/* ---------------- FORMATLAYICI FONKSİYONLAR ---------------- */

const formatInt = (v) =>
  Number(v || 0).toLocaleString("tr-TR", { maximumFractionDigits: 0 });

const formatCurrency = (v) =>
  Number(v || 0).toLocaleString("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }) + " TL";

const formatKg = (v) =>
  Number(v || 0).toLocaleString("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }) + " KG";

const formatEuro = (v) =>
  Number(v || 0).toLocaleString("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }) + " EUR";

/* ---------------- BASİT BAR CHART ---------------- */

function SimpleBarChart({
  data,
  valueKey,
  valueFormat,
  lastCount,
  getBarClassName,
  onBarClick,
  showValue = true,
}) {
  if (!data || data.length === 0) {
    return <div className="dash-chart-empty">Veri bulunamadı</div>;
  }

  const displayedData =
    lastCount && data.length > lastCount
      ? data.slice(data.length - lastCount)
      : data;

  const values = displayedData.map((d) => Number(d[valueKey] || 0));
  const maxVal = Math.max(...values) || 1;

  return (
    <div className="dash-chart-root">
      {displayedData.map((d, idx) => {
        const rawVal = Number(d[valueKey] || 0);
        const heightPct = (rawVal / maxVal) * 100;
        const extraClass = getBarClassName ? getBarClassName(d, rawVal) : "";

        return (
          <div key={idx} className="dash-chart-col">
            <div
              className="dash-chart-bar-wrapper"
              onClick={() => onBarClick && onBarClick(d)}
              style={{ cursor: onBarClick ? "pointer" : "default" }}
            >
              <div
                className={`dash-chart-bar ${extraClass}`}
                style={{ height: `${heightPct}%` }}
              />
            </div>

            <div className="dash-chart-month">{d.ayLabel || d.ay}</div>

            {showValue && (
              <div className="dash-chart-value">
                {valueFormat ? valueFormat(rawVal) : rawVal}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- BASİT PASTA (PIE) CHART ---------------- */

function SimplePieChart({
  data,
  valueKey,
  labelKey,
  size = 120,
  strokeWidth = 18,
  showLegend = true,
  onSliceClick,
}) {
  if (!data || data.length === 0) {
    return <div className="dash-chart-empty">Veri bulunamadı</div>;
  }

  const values = data.map((d) => Number(d[valueKey] || 0));
  const total = values.reduce((s, v) => s + v, 0);

  if (!total) {
    return <div className="dash-chart-empty">Bu yıl için veri yok</div>;
  }

  const colors = [
    "#22c55e",
    "#3b82f6",
    "#f97316",
    "#ec4899",
    "#eab308",
    "#a855f7",
    "#f43f5e",
    "#14b8a6",
    "#6b7280",
    "#0ea5e9",
  ];

  const radius = 50 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const svgSize = 100;

  let cumulative = 0;

  const slices = data.map((item, idx) => {
    const raw = Number(item[valueKey] || 0);
    const ratio = raw / total;
    const dashArray = `${ratio * circumference} ${circumference}`;
    const dashOffset = -cumulative * circumference;
    cumulative += ratio;

    const color = colors[idx % colors.length];
    const percent = (ratio * 100).toFixed(1);

    const formattedRaw = raw.toLocaleString("tr-TR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return {
      item,
      color,
      dashArray,
      dashOffset,
      percent,
      label: item[labelKey] || "",
      rawDisplay: formattedRaw,
    };
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{
          width: size || 120,
          height: size || 120,
          margin: "0 auto",
        }}
      >
        <svg viewBox={`0 0 ${svgSize} ${svgSize}`}>
          <circle
            cx={50}
            cy={50}
            r={radius}
            fill="none"
            stroke="#111827"
            strokeWidth={strokeWidth}
          />
          {slices.map((slice, idx) => (
            <circle
              key={idx}
              cx={50}
              cy={50}
              r={radius}
              fill="none"
              stroke={slice.color}
              strokeWidth={strokeWidth}
              strokeDasharray={slice.dashArray}
              strokeDashoffset={slice.dashOffset}
              style={{ transition: "stroke-dashoffset 0.3s ease" }}
              onClick={() => onSliceClick && onSliceClick(slice.item)}
              cursor={onSliceClick ? "pointer" : "default"}
            />
          ))}
        </svg>
      </div>

      {showLegend && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            maxHeight: 260,
            overflowY: "auto",
            fontSize: 12,
          }}
        >
          {slices.map((slice, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    background: slice.color,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {slice.label || "(Müşteri Yok)"}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  flexShrink: 0,
                  minWidth: 90,
                }}
              >
                <span style={{ opacity: 0.9 }}>{slice.percent}%</span>
                <span style={{ opacity: 0.8, fontSize: 11 }}>
                  {slice.rawDisplay}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- BAR DETAY PANEL ---------------- */

function DetailPanel({
  open,
  onClose,
  title,
  data,
  valueKey,
  valueFormat,
  years,
  year,
  onYearChange,
  getBarClassName,
}) {
  if (!open) return null;

  const filtered =
    year && data && data.length > 0
      ? data.filter((d) => {
          const ayStr = d.ay || d.Ay;
          if (!ayStr || ayStr.length < 4) return false;
          const y = parseInt(String(ayStr).substring(0, 4), 10);
          return y === year;
        })
      : data || [];

  let finalData = filtered;

  if (year && valueKey) {
    const monthDefs = [
      { no: 1, short: "Oca" },
      { no: 2, short: "Şub" },
      { no: 3, short: "Mar" },
      { no: 4, short: "Nis" },
      { no: 5, short: "May" },
      { no: 6, short: "Haz" },
      { no: 7, short: "Tem" },
      { no: 8, short: "Ağu" },
      { no: 9, short: "Eyl" },
      { no: 10, short: "Eki" },
      { no: 11, short: "Kas" },
      { no: 12, short: "Ara" },
    ];

    const map = {};
    filtered.forEach((d) => {
      const key = d.ay || d.Ay;
      if (key) {
        map[key] = d;
      }
    });

    const yy = String(year);
    const yy2 = yy.slice(-2);

    finalData = monthDefs.map((m) => {
      const ayStr = `${yy}-${String(m.no).padStart(2, "0")}`;
      const existing = map[ayStr];

      return {
        ay: ayStr,
        ayLabel: `${m.short} ${yy2}`,
        [valueKey]: existing ? Number(existing[valueKey] || 0) : 0,
      };
    });
  }

  return (
    <div className="detail-panel-backdrop" onClick={onClose}>
      <div
        className="detail-panel"
        style={{ width: "960px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="detail-panel-header">
          <div>
            <h2 className="detail-panel-title">{title}</h2>
            <p className="detail-panel-subtitle">
              Seçili yıl için Ocak–Aralık aylık dağılım.
            </p>
          </div>
        </div>

        <div className="detail-panel-toolbar">
          <label className="detail-panel-label">
            Yıl:
            <select
              value={year || ""}
              onChange={(e) =>
                onYearChange && onYearChange(Number(e.target.value))
              }
              className="detail-panel-select"
            >
              {years &&
                years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <div className="detail-panel-chart">
          <SimpleBarChart
            data={finalData}
            valueKey={valueKey}
            valueFormat={valueFormat}
            lastCount={12}
            getBarClassName={getBarClassName}
          />
        </div>
      </div>
    </div>
  );
}

/* ---------------- PIE DETAY PANEL ---------------- */

function PieDetailPanel({
  open,
  onClose,
  title,
  rawData,
  metricKey,
  years,
  year,
  onYearChange,
}) {
  if (!open) return null;

  const filtered =
    year && rawData && rawData.length > 0
      ? rawData.filter((d) => d.yil === year)
      : rawData || [];

  const pieData = (() => {
    if (!filtered || filtered.length === 0) return [];

    const arr = filtered
      .map((d) => ({
        musteri: d.musteri || "(Müşteri Yok)",
        value: Number(d[metricKey] || 0),
      }))
      .filter((x) => x.value > 0);

    if (arr.length === 0) return [];

    arr.sort((a, b) => b.value - a.value);

    const topCount = 8;
    if (arr.length > topCount) {
      const main = arr.slice(0, topCount - 1);
      const others = arr.slice(topCount - 1);
      const othersSum = others.reduce((s, x) => s + x.value, 0);
      if (othersSum > 0) {
        main.push({ musteri: "Diğer", value: othersSum });
      }
      return main;
    }

    return arr;
  })();

  const total = pieData.reduce((s, x) => s + x.value, 0);

  const colors = [
    "#22c55e",
    "#3b82f6",
    "#f97316",
    "#ec4899",
    "#eab308",
    "#a855f7",
    "#f43f5e",
    "#14b8a6",
    "#6b7280",
    "#0ea5e9",
  ];

  const formatValueWithUnit = (v) => {
    switch (metricKey) {
      case "toplamAdet":
        return `${formatInt(v)} Ad.`;
      case "toplamKg":
        return formatKg(v);
      case "toplamDovmeCiro":
      case "toplamCiro":
        return formatCurrency(v);
      default:
        return v?.toString() || "";
    }
  };

  return (
    <div className="detail-panel-backdrop" onClick={onClose}>
      <div
        className="detail-panel pie-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="detail-panel-header">
          <div>
            <h2 className="detail-panel-title">{title}</h2>
            <p className="detail-panel-subtitle">
              Seçili yıl için müşteri bazlı yüzde dağılım ve değerler.
            </p>
          </div>
          <button className="detail-panel-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="detail-panel-toolbar">
          <label className="detail-panel-label">
            Yıl:
            <select
              value={year || ""}
              onChange={(e) =>
                onYearChange && onYearChange(Number(e.target.value))
              }
              className="detail-panel-select"
            >
              {years &&
                years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <div className="detail-panel-chart pie-detail-layout">
          <div className="pie-detail-left">
            <SimplePieChart
              data={pieData}
              valueKey="value"
              labelKey="musteri"
              size={240}
              strokeWidth={26}
              showLegend={false}
            />
          </div>

          <div className="pie-detail-right">
            <div className="pie-detail-list-header">
              <span>Müşteri</span>
              <span>%</span>
              <span>Değer</span>
            </div>

            <div className="pie-detail-list-body">
              {pieData.map((item, idx) => {
                const percent = total ? (item.value / total) * 100 : 0;
                const color = colors[idx % colors.length];

                return (
                  <div key={idx} className="pie-detail-list-row">
                    <span className="pie-detail-name">
                      <span
                        className="pie-detail-color-dot"
                        style={{ backgroundColor: color }}
                      />
                      <span className="pie-detail-name-text">
                        {item.musteri || "(Müşteri Yok)"}
                      </span>
                    </span>
                    <span className="pie-detail-percent">
                      {percent.toFixed(1)}%
                    </span>
                    <span className="pie-detail-value">
                      {formatValueWithUnit(item.value)}
                    </span>
                  </div>
                );
              })}

              {pieData.length === 0 && (
                <div className="dash-chart-empty">
                  Seçili yıl için veri bulunamadı.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- YILLIK RAPOR DETAY PANELİ ---------------- */

function YearlyReportPanel({ open, onClose, rows, loading, error }) {
  if (!open) return null;

  const DEFAULT_FX_EUR = 40;
  const DEFAULT_FX_USD = 37;

  const formatTL = (v) => {
    const n = Number(v || 0);
    if (!n) return "—";
    return (
      "₺" +
      n.toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };

  const formatFX = (v, code) =>
    Number(v || 0).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ` ${code}`;

  const buildTooltip = (row, v) => {
    const n = Number(v || 0);
    if (!n) return "";

    const eurRate =
      (row && Number(row.ortalamaEUR || 0)) || DEFAULT_FX_EUR;
    const usdRate =
      (row && Number(row.ortalamaUSD || 0)) || DEFAULT_FX_USD;

    const eur = n / eurRate;
    const usd = n / usdRate;

    return `TL: ${formatTL(n)}
EUR (${eurRate.toFixed(4)}): ${formatFX(eur, "EUR")}
USD (${usdRate.toFixed(4)}): ${formatFX(usd, "USD")}`;
  };

  const safeRows = Array.isArray(rows) ? rows : [];

  return (
    <div className="detail-panel-backdrop" onClick={onClose}>
      <div
        className="detail-panel yearly-fullpanel"
        style={{
          width: "1200px",
          maxHeight: "50vh",
          borderRadius: "18px",
          boxShadow: "0 18px 45px rgba(0,0,0,0.55)",
          margin: "0 auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="detail-panel-header">
          <div>
            <h2 className="detail-panel-title">Yıllık Rapor Detay</h2>
            <p className="detail-panel-subtitle">
              Yıllara göre alış faturaları, gider kalemleri, satış faturası ve
              kar/zarar özet tablosu.
            </p>
          </div>
          <button className="detail-panel-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="detail-panel-chart">
          {loading ? (
            <div className="dash-chart-empty">Veri yükleniyor...</div>
          ) : error ? (
            <div
              className="dash-chart-empty"
              style={{ color: "#f97316", textAlign: "left" }}
            >
              Veri okunamadı: {error}
            </div>
          ) : (
            <div className="dash-table-wrapper">
              <table className="dash-table yearly-table">
                <thead>
                  <tr>
                    <th className="yearly-th-left">Yıl</th>
                    <th className="yearly-th-right">Alış Faturası</th>
                    <th className="yearly-th-right">Elektrik</th>
                    <th className="yearly-th-right">Maaş</th>
                    <th className="yearly-th-right">SGK</th>
                    <th className="yearly-th-right">Maliye</th>
                    <th className="yearly-th-right">Kira</th>
                    <th className="yearly-th-right">Faturasız</th>
                    <th className="yearly-th-right">Muhasebe</th>
                    <th className="yearly-th-right">İade/Fiyat Farkı</th>
                    <th className="yearly-th-right">Finans</th>
                    <th className="yearly-th-right">Toplam</th>
                    <th className="yearly-th-right">Satış Faturası</th>
                    <th className="yearly-th-right">Kar/Zarar</th>
                  </tr>
                </thead>
                <tbody>
                  {safeRows.map((r) => (
                    <tr key={r.yil}>
                      <td className="yearly-td-left">{r.yil}</td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.alisFaturasi)}
                      >
                        {formatTL(r.alisFaturasi)}
                      </td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.elektrik)}
                      >
                        {formatTL(r.elektrik)}
                      </td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.maasPersonel)}
                      >
                        {formatTL(r.maasPersonel)}
                      </td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.sgk)}
                      >
                        {formatTL(r.sgk)}
                      </td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.maliye)}
                      >
                        {formatTL(r.maliye)}
                      </td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.kira)}
                      >
                        {formatTL(r.kira)}
                      </td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.faturasiz)}
                      >
                        {formatTL(r.faturasiz)}
                      </td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.muhasebe)}
                      >
                        {formatTL(r.muhasebe)}
                      </td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.iadeFark)}
                      >
                        {formatTL(r.iadeFark)}
                      </td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.finans)}
                      >
                        {formatTL(r.finans)}
                      </td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.toplamGider)}
                      >
                        {formatTL(r.toplamGider)}
                      </td>
                      <td
                        className="yearly-td-right"
                        title={buildTooltip(r, r.satisFaturasi)}
                      >
                        {formatTL(r.satisFaturasi)}
                      </td>
                      <td
                        className="yearly-td-right"
                        style={{
                          color: r.karZarar >= 0 ? "#16a34a" : "#dc2626",
                          fontWeight: 600,
                        }}
                        title={buildTooltip(r, r.karZarar)}
                      >
                        {formatTL(r.karZarar)}
                      </td>
                    </tr>
                  ))}
                  {safeRows.length === 0 && (
                    <tr>
                      <td colSpan={14} className="dash-chart-empty">
                        Gösterilecek veri bulunamadı.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- SATIŞ FATURALARI PANELİ ---------------- */

function SatisFaturaPanel({ open, onClose, rows, loading, error }) {
  if (!open) return null;

  const DEFAULT_FX_EUR = 40;
  const DEFAULT_FX_USD = 37;

  const formatTL = (v) => {
    const n = Number(v || 0);
    if (!n) return "—";
    return (
      "₺" +
      n.toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };

  const formatFX = (v, code) =>
    Number(v || 0).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ` ${code}`;

  const buildTooltip = (row, tlValue) => {
    const n = Number(tlValue || 0);
    if (!n) return "";

    const eurRate =
      (row && Number(row.ortalamaEUR || 0)) || DEFAULT_FX_EUR;
    const usdRate =
      (row && Number(row.ortalamaUSD || 0)) || DEFAULT_FX_USD;

    const eur = n / eurRate;
    const usd = n / usdRate;

    return `TL: ${formatTL(n)}
EUR (${eurRate.toFixed(4)}): ${formatFX(eur, "EUR")}
USD (${usdRate.toFixed(4)}): ${formatFX(usd, "USD")}`;
  };

  const safeRows = Array.isArray(rows) ? rows : [];
  const sortedRows = [...safeRows].sort((a, b) => (b.yil || 0) - (a.yil || 0));

  return (
    <div className="detail-panel-backdrop" onClick={onClose}>
      <div
        className="detail-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "820px",
          maxWidth: "96%",
          borderRadius: "20px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.65)",
          border: "1px solid #1f2937",
        }}
      >
        <div className="detail-panel-header">
          <div>
            <h2 className="detail-panel-title">Satış Faturaları – Yıllık Özet</h2>
            <p className="detail-panel-subtitle">
              Yıllara göre net toplam, KDV ve genel toplam, TL ve döviz
              karşılıkları.
            </p>
          </div>
          <button className="detail-panel-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="detail-panel-chart">
          {loading ? (
            <div className="dash-chart-empty">Veri yükleniyor...</div>
          ) : error ? (
            <div
              className="dash-chart-empty"
              style={{ color: "#f97316", textAlign: "left" }}
            >
              Veri okunamadı: {error}
            </div>
          ) : (
            <div className="dash-table-wrapper">
              <table
                className="dash-table yearly-table"
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <thead>
                  <tr>
                    <th
                      className="yearly-th-left"
                      style={{ width: "18%", textAlign: "left" }}
                    >
                      Yıl
                    </th>
                    <th
                      className="yearly-th-right"
                      style={{ width: "27%", textAlign: "right" }}
                    >
                      Net Toplam
                    </th>
                    <th
                      className="yearly-th-right"
                      style={{ width: "27%", textAlign: "right" }}
                    >
                      KDV Toplamı
                    </th>
                    <th
                      className="yearly-th-right"
                      style={{ width: "28%", textAlign: "right" }}
                    >
                      Genel Toplam
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((r) => (
                    <tr key={r.yil}>
                      <td className="yearly-td-left">{r.yil}</td>
                      <td
                        className="yearly-td-right"
                        style={{ textAlign: "right" }}
                        title={buildTooltip(r, r.netToplam)}
                      >
                        {formatTL(r.netToplam)}
                      </td>
                      <td
                        className="yearly-td-right"
                        style={{ textAlign: "right" }}
                        title={buildTooltip(r, r.kdvToplami)}
                      >
                        {formatTL(r.kdvToplami)}
                      </td>
                      <td
                        className="yearly-td-right"
                        style={{ textAlign: "right" }}
                        title={buildTooltip(r, r.genelToplam)}
                      >
                        {formatTL(r.genelToplam)}
                      </td>
                    </tr>
                  ))}

                  {sortedRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="dash-chart-empty">
                        Gösterilecek veri bulunamadı.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- ALIŞ FATURALARI PANELİ ---------------- */

function AlisFaturaPanel({ open, onClose, rows, loading, error }) {
  if (!open) return null;

  const DEFAULT_FX_EUR = 40;
  const DEFAULT_FX_USD = 37;

  const formatTL = (v) => {
    const n = Number(v || 0);
    if (!n) return "—";
    return (
      "₺" +
      n.toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };

  const formatFX = (v, code) =>
    Number(v || 0).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ` ${code}`;

  const buildTooltip = (row, tlValue) => {
    const n = Number(tlValue || 0);
    if (!n) return "";

    const eurRate =
      (row && Number(row.ortalamaEUR || 0)) || DEFAULT_FX_EUR;
    const usdRate =
      (row && Number(row.ortalamaUSD || 0)) || DEFAULT_FX_USD;

    const eur = n / eurRate;
    const usd = n / usdRate;

    return `TL: ${formatTL(n)}
EUR (${eurRate.toFixed(4)}): ${formatFX(eur, "EUR")}
USD (${usdRate.toFixed(4)}): ${formatFX(usd, "USD")}`;
  };

  const safeRows = Array.isArray(rows) ? rows : [];
  const sortedRows = [...safeRows].sort((a, b) => (b.yil || 0) - (a.yil || 0));

  return (
    <div className="detail-panel-backdrop" onClick={onClose}>
      <div
        className="detail-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "820px",
          maxWidth: "96%",
          borderRadius: "20px",
          boxShadow: "0 24px 60px rgba(0,0,0,0.65)",
          border: "1px solid #1f2937",
        }}
      >
        <div className="detail-panel-header">
          <div>
            <h2 className="detail-panel-title">Alış Faturaları – Yıllık Özet</h2>
            <p className="detail-panel-subtitle">
              Yıllara göre net toplam, KDV ve genel toplam, TL ve döviz
              karşılıkları.
            </p>
          </div>
          <button className="detail-panel-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="detail-panel-chart">
          {loading ? (
            <div className="dash-chart-empty">Veri yükleniyor...</div>
          ) : error ? (
            <div
              className="dash-chart-empty"
              style={{ color: "#f97316", textAlign: "left" }}
            >
              Veri okunamadı: {error}
            </div>
          ) : (
            <div className="dash-table-wrapper">
              <table
                className="dash-table yearly-table"
                style={{ tableLayout: "fixed", width: "100%" }}
              >
                <thead>
                  <tr>
                    <th
                      className="yearly-th-left"
                      style={{ width: "18%", textAlign: "left" }}
                    >
                      Yıl
                    </th>
                    <th
                      className="yearly-th-right"
                      style={{ width: "27%", textAlign: "right" }}
                    >
                      Net Toplam
                    </th>
                    <th
                      className="yearly-th-right"
                      style={{ width: "27%", textAlign: "right" }}
                    >
                      KDV Toplamı
                    </th>
                    <th
                      className="yearly-th-right"
                      style={{ width: "28%", textAlign: "right" }}
                    >
                      Genel Toplam
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((r) => (
                    <tr key={r.yil}>
                      <td className="yearly-td-left">{r.yil}</td>
                      <td
                        className="yearly-td-right"
                        style={{ textAlign: "right" }}
                        title={buildTooltip(r, r.netToplam)}
                      >
                        {formatTL(r.netToplam)}
                      </td>
                      <td
                        className="yearly-td-right"
                        style={{ textAlign: "right" }}
                        title={buildTooltip(r, r.kdvToplami)}
                      >
                        {formatTL(r.kdvToplami)}
                      </td>
                      <td
                        className="yearly-td-right"
                        style={{ textAlign: "right" }}
                        title={buildTooltip(r, r.genelToplam)}
                      >
                        {formatTL(r.genelToplam)}
                      </td>
                    </tr>
                  ))}

                  {sortedRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="dash-chart-empty">
                        Gösterilecek veri bulunamadı.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- AÇIK SİPARİŞLER PANELİ ---------------- */

function OpenOrdersPanel({ open, onClose, rows, loading, error }) {
  if (!open) return null;

  const safeRows = Array.isArray(rows) ? rows : [];

  return (
    <div className="detail-panel-backdrop" onClick={onClose}>
      <div
        className="detail-panel yearly-fullpanel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="detail-panel-header">
          <div>
            <h2 className="detail-panel-title">Açık Siparişler Listesi</h2>
            <p className="detail-panel-subtitle">
              Sipariş listesi (filtreyi sonra düşüneceğiz).
            </p>
          </div>
          <button className="detail-panel-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="detail-panel-chart">
          {loading ? (
            <div className="dash-chart-empty">Veri yükleniyor...</div>
          ) : error ? (
            <div
              className="dash-chart-empty"
              style={{ color: "#f97316", textAlign: "left" }}
            >
              Veri okunamadı: {error}
            </div>
          ) : safeRows.length === 0 ? (
            <div className="dash-chart-empty">
              Gösterilecek sipariş bulunamadı.
            </div>
          ) : (
            <div className="dash-table-wrapper">
              <table className="dash-table yearly-table">
                <thead>
                  <tr>
                    <th className="yearly-th-left">Haksip No</th>
                    <th className="yearly-th-left">Müşteri</th>
                    <th className="yearly-th-left">Erf</th>
                    <th className="yearly-th-left">Parça Adı</th>
                    <th className="yearly-th-right">Sip. Adet</th>
                    <th className="yearly-th-left">Teslim Tarihi</th>
                  </tr>
                </thead>
                <tbody>
                  {safeRows.map((r) => (
                    <tr key={r.Kimlik}>
                      <td className="yearly-td-left">{r.HaksipNo}</td>
                      <td className="yearly-td-left">{r.Musteri}</td>
                      <td className="yearly-td-left">{r.Erf}</td>
                      <td className="yearly-td-left">{r.ParcaAdi}</td>
                      <td className="yearly-td-right">
                        {formatInt(r.SipAdet)}
                      </td>
                      <td className="yearly-td-left">
                        {r.TesTarih ? String(r.TesTarih).substring(0, 10) : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- İŞÇİLİK KAR/ZARAR PANELİ ---------------- */

function IscilikKarPanel({ open, onClose, rows, loading, error }) {
  if (!open) return null;

  const DEFAULT_FX_EUR = 40;
  const DEFAULT_FX_USD = 37;

  const formatTL = (v) =>
    "₺" +
    Number(v || 0).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatFX = (v, code) =>
    Number(v || 0).toLocaleString("tr-TR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ` ${code}`;

  const buildTooltip = (row, tlValue) => {
    const n = Number(tlValue || 0);
    if (!n) return "";

    const eurRate =
      (row && Number(row.ortalamaEUR || 0)) || DEFAULT_FX_EUR;
    const usdRate =
      (row && Number(row.ortalamaUSD || 0)) || DEFAULT_FX_USD;

    const eur = n / eurRate;
    const usd = n / usdRate;

    return `TL: ${formatTL(n)}
EUR (${eurRate.toFixed(4)}): ${formatFX(eur, "EUR")}
USD (${usdRate.toFixed(4)}): ${formatFX(usd, "USD")}`;
  };

  const safeRows = Array.isArray(rows) ? rows : [];
  const displayRows =
    safeRows.length > 5 ? safeRows.slice(safeRows.length - 5) : safeRows;

  return (
    <div className="detail-panel-backdrop" onClick={onClose}>
      <div
        className="detail-panel iscilik-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="detail-panel-header">
          <div>
            <h2 className="detail-panel-title">İşçilik Kar/Zarar Özeti</h2>
            <p className="detail-panel-subtitle">
              Yıllara göre dövülen adet/kg, cirolar ve giderler.
            </p>
          </div>
          <button className="detail-panel-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="detail-panel-chart">
          {loading ? (
            <div className="dash-chart-empty">Veri yükleniyor...</div>
          ) : error ? (
            <div
              className="dash-chart-empty"
              style={{ color: "#f97316", textAlign: "left" }}
            >
              Veri okunamadı: {error}
            </div>
          ) : displayRows.length === 0 ? (
            <div className="dash-chart-empty">
              Gösterilecek işçilik özeti bulunamadı.
            </div>
          ) : (
            <div className="dash-table-wrapper">
              <table
                className="dash-table iscilik-table"
                style={{ fontSize: 12, borderCollapse: "collapse" }}
              >
                <thead>
                  <tr>
                    <th style={{ padding: "6px 14px", textAlign: "left" }}>
                      Yıl
                    </th>
                    <th style={{ padding: "6px 14px", textAlign: "right" }}>
                      Dövülen Adet
                    </th>
                    <th style={{ padding: "6px 14px", textAlign: "right" }}>
                      Dövülen KG
                    </th>
                    <th style={{ padding: "6px 14px", textAlign: "right" }}>
                      Dövme Ciro (TL)
                    </th>
                    <th style={{ padding: "6px 14px", textAlign: "right" }}>
                      Toplam Ciro (TL)
                    </th>
                    <th style={{ padding: "6px 14px", textAlign: "right" }}>
                      İşçilik Gideri (TL)
                    </th>
                    <th style={{ padding: "6px 14px", textAlign: "right" }}>
                      Yönetim Gideri (TL)
                    </th>
                    <th style={{ padding: "6px 14px", textAlign: "right" }}>
                      Yatırım Gideri (TL)
                    </th>
                    <th style={{ padding: "6px 14px", textAlign: "right" }}>
                      Kar/Zarar (TL)
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {displayRows.map((r) => {
                    const isPositive = (r.karZarar || 0) >= 0;
                    const rowBg = isPositive
                      ? "rgba(22,163,74,0.12)"
                      : "rgba(220,38,38,0.12)";

                    return (
                      <tr
                        key={r.yil}
                        style={{
                          background: rowBg,
                        }}
                      >
                        <td style={{ padding: "4px 14px" }}>{r.yil}</td>

                        <td
                          style={{ padding: "4px 14px" }}
                          className="dash-text-right"
                          title={`Dövülen Adet: ${formatInt(r.dovulenAdet)}`}
                        >
                          {formatInt(r.dovulenAdet)}
                        </td>
                        <td
                          style={{ padding: "4px 14px" }}
                          className="dash-text-right"
                          title={`Dövülen KG: ${formatKg(r.dovulenKg)}`}
                        >
                          {formatKg(r.dovulenKg)}
                        </td>

                        <td
                          style={{ padding: "4px 14px" }}
                          className="dash-text-right"
                          title={buildTooltip(r, r.dovmeCiroTL)}
                        >
                          {formatTL(r.dovmeCiroTL)}
                        </td>
                        <td
                          style={{ padding: "4px 14px" }}
                          className="dash-text-right"
                          title={buildTooltip(r, r.toplamCiroTL)}
                        >
                          {formatTL(r.toplamCiroTL)}
                        </td>
                        <td
                          style={{ padding: "4px 14px" }}
                          className="dash-text-right"
                          title={buildTooltip(r, r.iscilikGiderTL)}
                        >
                          {formatTL(r.iscilikGiderTL)}
                        </td>
                        <td
                          style={{ padding: "4px 14px" }}
                          className="dash-text-right"
                          title={buildTooltip(r, r.yonetimGiderTL)}
                        >
                          {formatTL(r.yonetimGiderTL)}
                        </td>
                        <td
                          style={{ padding: "4px 14px" }}
                          className="dash-text-right"
                          title={buildTooltip(r, r.yatirimGiderTL)}
                        >
                          {formatTL(r.yatirimGiderTL)}
                        </td>
                        <td
                          style={{
                            padding: "4px 14px",
                            fontWeight: 600,
                            color: isPositive ? "#16a34a" : "#dc2626",
                          }}
                          className="dash-text-right"
                          title={buildTooltip(r, r.karZarar)}
                        >
                          {formatTL(r.karZarar)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- ANA DASHBOARD ---------------- */

function HomeDashboard({ apiBase, user }) {
  //const API_BASE =
   // apiBase ||
    //(window.location.hostname.includes("apphaker.com")
    // ? "http://195.87.235.112:7260"
     // : "http://192.168.2.230:7260");

      const API_BASE = apiBase || "https://api.apphaker.com";

  // KPI
  const [acikSiparisAdet, setAcikSiparisAdet] = useState(0);
  const [acikSiparisTutar, setAcikSiparisTutar] = useState(0);
  const [acikSiparisLoading, setAcikSiparisLoading] = useState(false);
  const [acikSiparisError, setAcikSiparisError] = useState(null);

  const [bekleyenAdet, setBekleyenAdet] = useState(0);
  const [bekleyenTutar, setBekleyenTutar] = useState(0);
  const [bekleyenLoading, setBekleyenLoading] = useState(false);
  const [bekleyenError, setBekleyenError] = useState(null);

  const [stokKg, setStokKg] = useState(0);
  const [stokTutar, setStokTutar] = useState(0);
  const [musteriMalzemeKg, setMusteriMalzemeKg] = useState(0);
  const [stokLoading, setStokLoading] = useState(false);
  const [stokError, setStokError] = useState(null);

  const [kalipAdet, setKalipAdet] = useState(0);
  const [kalipDegerEuro, setKalipDegerEuro] = useState(0);
  const [kalipLoading, setKalipLoading] = useState(false);
  const [kalipError, setKalipError] = useState(null);

  // Aylık seri (bar grafikleri)
  const [adetData, setAdetData] = useState([]);
  const [adetLoading, setAdetLoading] = useState(false);
  const [adetError, setAdetError] = useState(null);

  const [kgData, setKgData] = useState([]);
  const [kgLoading, setKgLoading] = useState(false);
  const [kgError, setKgError] = useState(null);

  const [dovmeCiroData, setDovmeCiroData] = useState([]);
  const [dovmeCiroLoading, setDovmeCiroLoading] = useState(false);
  const [dovmeCiroError, setDovmeCiroError] = useState(null);

  const [giderData, setGiderData] = useState([]);
  const [giderLoading, setGiderLoading] = useState(false);
  const [giderError, setGiderError] = useState(null);

  // Müşteri yıllık özet (pie)
  const [customerYearData, setCustomerYearData] = useState([]);
  const [customerYearLoading, setCustomerYearLoading] = useState(false);
  const [customerYearError, setCustomerYearError] = useState(null);

  // Yıllık rapor detay
  const [yearlyRows, setYearlyRows] = useState([]);
  const [yearlyLoading, setYearlyLoading] = useState(false);
  const [yearlyError, setYearlyError] = useState(null);

  // İşçilik kar/zarar özeti
  const [iscilikRows, setIscilikRows] = useState([]);
  const [iscilikLoading, setIscilikLoading] = useState(false);
  const [iscilikError, setIscilikError] = useState(null);

  // Satış faturaları yıllık özet
  const [satisRows, setSatisRows] = useState([]);
  const [satisLoading, setSatisLoading] = useState(false);
  const [satisError, setSatisError] = useState(null);

  // Alış faturaları özeti
  const [alisRows, setAlisRows] = useState([]);
  const [alisLoading, setAlisLoading] = useState(false);
  const [alisError, setAlisError] = useState(null);

  // Paneller
  const [alisPanelOpen, setAlisPanelOpen] = useState(false);
  const [openOrdersPanelOpen, setOpenOrdersPanelOpen] = useState(false);
  const [openOrdersRows, setOpenOrdersRows] = useState([]);
  const [openOrdersLoading, setOpenOrdersLoading] = useState(false);
  const [openOrdersError, setOpenOrdersError] = useState(null);

  // Bar detay panel
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailMetric, setDetailMetric] = useState(null);
  const [detailYears, setDetailYears] = useState([]);
  const [detailYear, setDetailYear] = useState(null);

  // Pie detay panel
  const [pieOpen, setPieOpen] = useState(false);
  const [pieMetric, setPieMetric] = useState(null);
  const [pieYears, setPieYears] = useState([]);
  const [pieYear, setPieYear] = useState(null);

  // Diğer paneller
  const [yearlyPanelOpen, setYearlyPanelOpen] = useState(false);
  const [iscilikPanelOpen, setIscilikPanelOpen] = useState(false);
  const [satisPanelOpen, setSatisPanelOpen] = useState(false);

  /* ---- YARDIMCI FONKSİYONLAR (YIL LİSTESİ) ---- */

  const extractYearsFromAyData = (data) => {
    const set = new Set();
    (data || []).forEach((d) => {
      const ayStr = d.ay || d.Ay;
      if (!ayStr || String(ayStr).length < 4) return;
      const y = parseInt(String(ayStr).substring(0, 4), 10);
      if (!isNaN(y)) set.add(y);
    });
    return Array.from(set).sort((a, b) => b - a);
  };

  const extractYearsFromCustomer = (data) => {
    const set = new Set();
    (data || []).forEach((d) => {
      if (typeof d.yil === "number") {
        set.add(d.yil);
      }
    });
    return Array.from(set).sort((a, b) => b - a);
  };

  /* ---- BAR DETAY PANELİNİ AÇ ---- */

  const openDetail = (metric, clickedItem) => {
    let sourceData = [];
    switch (metric) {
      case "adet":
        sourceData = adetData;
        break;
      case "kg":
        sourceData = kgData;
        break;
      case "dovmeCiro":
        sourceData = dovmeCiroData;
        break;
      case "gider":
        sourceData = giderData;
        break;
      default:
        sourceData = [];
    }

    const years = extractYearsFromAyData(sourceData);
    let defaultYear = null;

    if (clickedItem && (clickedItem.ay || clickedItem.Ay)) {
      const ayStr = clickedItem.ay || clickedItem.Ay;
      const y = parseInt(String(ayStr).substring(0, 4), 10);
      if (!isNaN(y)) defaultYear = y;
    }

    if (!defaultYear && years.length > 0) {
      defaultYear = years[0];
    }

    setDetailMetric(metric);
    setDetailYears(years);
    setDetailYear(defaultYear || null);
    setDetailOpen(true);
  };

  const closeDetail = () => setDetailOpen(false);

  /* ---- PIE DETAY PANELİNİ AÇ ---- */

  const openPieDetail = (metric) => {
    const years = extractYearsFromCustomer(customerYearData);
    let defaultYear = null;

    if (years.length > 0) {
      defaultYear = years[0];
    }

    setPieMetric(metric);
    setPieYears(years);
    setPieYear(defaultYear || null);
    setPieOpen(true);
  };

  const closePieDetail = () => setPieOpen(false);

  /* ---- API ÇAĞRILARI ---- */

  // KPI: Açık sipariş
  useEffect(() => {
    const fetchAcik = async () => {
      try {
        setAcikSiparisLoading(true);
        setAcikSiparisError(null);

        const res = await fetch(`${API_BASE}/api/orders/acik-siparis-ozet`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setAcikSiparisAdet(data.acikSiparisAdet ?? 0);
        setAcikSiparisTutar(data.acikSiparisTutar ?? 0);
      } catch (err) {
        console.error("Açık sipariş özeti okunamadı:", err);
        setAcikSiparisError(err.message);
        setAcikSiparisAdet(0);
        setAcikSiparisTutar(0);
      } finally {
        setAcikSiparisLoading(false);
      }
    };

    fetchAcik();
  }, [API_BASE]);

  // KPI: Bekleyen ürün
  useEffect(() => {
    const fetchBekleyen = async () => {
      try {
        setBekleyenLoading(true);
        setBekleyenError(null);

        const res = await fetch(`${API_BASE}/api/orders/bekleyen-ozet`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setBekleyenAdet(data.bekleyenAdet ?? 0);
        setBekleyenTutar(data.bekleyenTutar ?? 0);
      } catch (err) {
        console.error("Bekleyen ürün özeti okunamadı:", err);
        setBekleyenError(err.message);
        setBekleyenAdet(0);
        setBekleyenTutar(0);
      } finally {
        setBekleyenLoading(false);
      }
    };

    fetchBekleyen();
  }, [API_BASE]);

  // KPI: Stok
  useEffect(() => {
    const fetchStok = async () => {
      try {
        setStokLoading(true);
        setStokError(null);

        const res = await fetch(`${API_BASE}/api/orders/stok-ozet`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setStokKg(data.hakerStokKg ?? 0);
        setStokTutar(data.hakerStokTutar ?? 0);
        setMusteriMalzemeKg(data.musteriMalzemeKg ?? 0);
      } catch (err) {
        console.error("Stok özeti okunamadı:", err);
        setStokError(err.message);
        setStokKg(0);
        setStokTutar(0);
        setMusteriMalzemeKg(0);
      } finally {
        setStokLoading(false);
      }
    };

    fetchStok();
  }, [API_BASE]);

  // KPI: Kalıp
  useEffect(() => {
    const fetchKalip = async () => {
      try {
        setKalipLoading(true);
        setKalipError(null);

        const res = await fetch(`${API_BASE}/api/orders/kalip-ozet`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setKalipAdet(data.kalipAdet ?? 0);
        setKalipDegerEuro(data.kalipDegerEuro ?? 0);
      } catch (err) {
        console.error("Kalıp özeti okunamadı:", err);
        setKalipError(err.message);
        setKalipAdet(0);
        setKalipDegerEuro(0);
      } finally {
        setKalipLoading(false);
      }
    };

    fetchKalip();
  }, [API_BASE]);

  // Aylık üretim adet
  useEffect(() => {
    const fetchAylikAdet = async () => {
      try {
        setAdetLoading(true);
        setAdetError(null);

        const res = await fetch(`${API_BASE}/api/orders/aylik-uretim-adet`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setAdetData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Aylık üretim adet verisi okunamadı:", err);
        setAdetError(err.message);
        setAdetData([]);
      } finally {
        setAdetLoading(false);
      }
    };

    fetchAylikAdet();
  }, [API_BASE]);

  // Aylık üretim kg
  useEffect(() => {
    const fetchAylikKg = async () => {
      try {
        setKgLoading(true);
        setKgError(null);

        const res = await fetch(`${API_BASE}/api/orders/aylik-uretim-kg`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setKgData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Aylık üretim kg verisi okunamadı:", err);
        setKgError(err.message);
        setKgData([]);
      } finally {
        setKgLoading(false);
      }
    };

    fetchAylikKg();
  }, [API_BASE]);

  // Aylık dövme işçilik cirosu
  useEffect(() => {
    const fetchDovmeCiro = async () => {
      try {
        setDovmeCiroLoading(true);
        setDovmeCiroError(null);

        const res = await fetch(`${API_BASE}/api/orders/aylik-dovme-ciro`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setDovmeCiroData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Aylık dövme işçilik cirosu verisi okunamadı:", err);
        setDovmeCiroError(err.message);
        setDovmeCiroData([]);
      } finally {
        setDovmeCiroLoading(false);
      }
    };

    fetchDovmeCiro();
  }, [API_BASE]);

  // Aylık gider
  useEffect(() => {
    const fetchGider = async () => {
      try {
        setGiderLoading(true);
        setGiderError(null);

        const res = await fetch(`${API_BASE}/api/orders/aylik-gider`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setGiderData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Aylık gider verisi okunamadı:", err);
        setGiderError(err.message);
        setGiderData([]);
      } finally {
        setGiderLoading(false);
      }
    };

    fetchGider();
  }, [API_BASE]);

  // Müşteri yıllık özet (pie için)
  useEffect(() => {
    const fetchCustomerYear = async () => {
      try {
        setCustomerYearLoading(true);
        setCustomerYearError(null);

        const res = await fetch(`${API_BASE}/api/orders/musteri-yillik-ozet`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setCustomerYearData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Müşteri yıllık özet okunamadı:", err);
        setCustomerYearError(err.message);
        setCustomerYearData([]);
      } finally {
        setCustomerYearLoading(false);
      }
    };

    fetchCustomerYear();
  }, [API_BASE]);

  // Yıllık gider / satış (Yıllık Rapor Detay paneli)
  useEffect(() => {
    const fetchYearly = async () => {
      try {
        setYearlyLoading(true);
        setYearlyError(null);

        const res = await fetch(`${API_BASE}/api/orders/yillik-gider-ozet`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setYearlyRows(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Yıllık gider özeti okunamadı:", err);
        setYearlyError(err.message);
        setYearlyRows([]);
      } finally {
        setYearlyLoading(false);
      }
    };

    fetchYearly();
  }, [API_BASE]);

  // Satış faturaları yıllık özet
  useEffect(() => {
    const fetchSatis = async () => {
      try {
        setSatisLoading(true);
        setSatisError(null);

        const res = await fetch(`${API_BASE}/api/orders/satis-fatura-ozet`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setSatisRows(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Satış faturaları özeti okunamadı:", err);
        setSatisError(err.message);
        setSatisRows([]);
      } finally {
        setSatisLoading(false);
      }
    };

    fetchSatis();
  }, [API_BASE]);

  // Alış faturaları özeti
  useEffect(() => {
    const fetchAlis = async () => {
      try {
        setAlisLoading(true);
        setAlisError(null);

        const res = await fetch(`${API_BASE}/api/orders/alis-fatura-ozet`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setAlisRows(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Alış fatura özeti okunamadı:", err);
        setAlisError(err.message);
        setAlisRows([]);
      } finally {
        setAlisLoading(false);
      }
    };

    fetchAlis();
  }, [API_BASE]);

  // İşçilik kar/zarar özeti
  useEffect(() => {
    const fetchIscilik = async () => {
      try {
        setIscilikLoading(true);
        setIscilikError(null);

        const res = await fetch(`${API_BASE}/api/orders/iscilik-kar-ozet`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setIscilikRows(
          Array.isArray(data)
            ? data.sort((a, b) => (b.yil || 0) - (a.yil || 0))
            : []
        );
      } catch (err) {
        console.error("İşçilik kar/zarar özeti okunamadı:", err);
        setIscilikError(err.message);
        setIscilikRows([]);
      } finally {
        setIscilikLoading(false);
      }
    };

    fetchIscilik();
  }, [API_BASE]);

  // Açık siparişler listesi
  useEffect(() => {
    const fetchOpenOrders = async () => {
      try {
        setOpenOrdersLoading(true);
        setOpenOrdersError(null);

        const res = await fetch(`${API_BASE}/api/orders`);
        if (!res.ok) throw new Error(`API hata kodu: ${res.status}`);

        const data = await res.json();
        setOpenOrdersRows(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Açık sipariş listesi okunamadı:", err);
        setOpenOrdersError(err.message);
        setOpenOrdersRows([]);
      } finally {
        setOpenOrdersLoading(false);
      }
    };

    fetchOpenOrders();
  }, [API_BASE]);

  /* ---- MINI BARCHART DATA ---- */

  const miniRefMonths = useMemo(() => {
    if (!adetData || adetData.length === 0) return [];
    return adetData.length > 6
      ? adetData.slice(adetData.length - 6)
      : adetData;
  }, [adetData]);

  const buildAlignedMiniData = (refMonths, srcData, valueKey) => {
    if (!refMonths || refMonths.length === 0) return [];
    const map = {};
    (srcData || []).forEach((d) => {
      if (d.ay) {
        map[d.ay] = d;
      }
    });
    return refMonths.map((ref) => {
      const match = map[ref.ay];
      const rawVal = match ? Number(match[valueKey] || 0) : 0;
      return {
        ay: ref.ay,
        ayLabel: ref.ayLabel,
        [valueKey]: rawVal,
      };
    });
  };

  const miniAdetData = miniRefMonths;
  const miniKgData = buildAlignedMiniData(miniRefMonths, kgData, "toplamKg");
  const miniDovmeCiroData = buildAlignedMiniData(
    miniRefMonths,
    dovmeCiroData,
    "toplamDovmeCiro"
  );
  const miniGiderData = buildAlignedMiniData(
    miniRefMonths,
    giderData,
    "toplamGider"
  );

  const giderMapForMini = {};
  miniGiderData.forEach((g) => {
    if (g.ay) {
      giderMapForMini[g.ay] = Number(g.toplamGider || 0);
    }
  });

  /* ---- PIE İÇİN YIL SEÇİMİ ---- */

  const allYearsFromAdet = extractYearsFromAyData(adetData);
  const allYearsFromCustomer = extractYearsFromCustomer(customerYearData);

  const currentYear =
    allYearsFromAdet.length > 0
      ? allYearsFromAdet[0]
      : allYearsFromCustomer.length > 0
      ? allYearsFromCustomer[0]
      : new Date().getFullYear();

  const buildPieDataForMetric = (metricKey) => {
    if (!customerYearData || customerYearData.length === 0) return [];

    const filtered = customerYearData.filter((d) => d.yil === currentYear);
    if (filtered.length === 0) return [];

    const arr = filtered
      .map((d) => ({
        musteri: d.musteri || "(Müşteri Yok)",
        value: Number(d[metricKey] || 0),
      }))
      .filter((x) => x.value > 0);

    if (arr.length === 0) return [];

    arr.sort((a, b) => b.value - a.value);

    const topCount = 8;
    if (arr.length > topCount) {
      const main = arr.slice(0, topCount - 1);
      const others = arr.slice(topCount - 1);
      const othersSum = others.reduce((s, x) => s + x.value, 0);
      if (othersSum > 0) {
        main.push({ musteri: "Diğer", value: othersSum });
      }
      return main;
    }

    return arr;
  };

  const pieAdetData = buildPieDataForMetric("toplamAdet");
  const pieKgData = buildPieDataForMetric("toplamKg");
  const pieDovmeCiroData = buildPieDataForMetric("toplamDovmeCiro");
  const pieToplamCiroData = buildPieDataForMetric("toplamCiro");

  /* ---- BAR DETAY PANELİ İÇİN BAŞLIK / FMT ---- */

  let detailTitle = "";
  let detailData = [];
  let detailValueKey = "";
  let detailFormat = (v) => v;

  switch (detailMetric) {
    case "adet":
      detailTitle = "Yıllık Üretim Adedi";
      detailData = adetData;
      detailValueKey = "toplamAdet";
      detailFormat = (v) => formatInt(v) + " Ad.";
      break;
    case "kg":
      detailTitle = "Yıllık Üretim KG";
      detailData = kgData;
      detailValueKey = "toplamKg";
      detailFormat = (v) =>
        Number(v || 0).toLocaleString("tr-TR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }) + " KG";
      break;
    case "dovmeCiro":
      detailTitle = "Yıllık Dövme İşçilik Cirosu";
      detailData = dovmeCiroData;
      detailValueKey = "toplamDovmeCiro";
      detailFormat = (v) =>
        Number(v || 0).toLocaleString("tr-TR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }) + " TL";
      break;
    case "gider":
      detailTitle = "Yıllık İşçilik Gideri";
      detailData = giderData;
      detailValueKey = "toplamGider";
      detailFormat = (v) =>
        Number(v || 0).toLocaleString("tr-TR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }) + " TL";
      break;
    default:
      detailTitle = "";
      detailData = [];
      detailValueKey = "";
      detailFormat = (v) => v;
  }

  let detailBarClassName = null;

  if (detailMetric === "dovmeCiro") {
    detailBarClassName = (item, ciroVal) => {
      const ayKey = item.ay || item.Ay;
      const giderRow =
        (giderData || []).find((g) => {
          const gAy = g.ay || g.Ay;
          return gAy === ayKey;
        }) || null;

      const giderVal = giderRow ? Number(giderRow.toplamGider || 0) : 0;

      if (giderVal > ciroVal) {
        return "bar-ciro-bad";
      }
      return "bar-ciro-ok";
    };
  } else if (detailMetric === "kg") {
    detailBarClassName = () => "bar-kg";
  } else if (detailMetric === "gider") {
    detailBarClassName = () => "bar-gider";
  }

  /* ---- PIE DETAY PANELİ BAŞLIK / METRİK ---- */

  let pieTitle = "";
  let pieMetricKey = "";

  switch (pieMetric) {
    case "adet":
      pieTitle = `Müşteri Dağılımı – Üretim Adedi`;
      pieMetricKey = "toplamAdet";
      break;
    case "kg":
      pieTitle = `Müşteri Dağılımı – Üretim KG`;
      pieMetricKey = "toplamKg";
      break;
    case "dovmeCiro":
      pieTitle = `Müşteri Dağılımı – Dövme İşçilik Cirosu`;
      pieMetricKey = "toplamDovmeCiro";
      break;
    case "toplamCiro":
      pieTitle = `Müşteri Dağılımı – Toplam Ciro`;
      pieMetricKey = "toplamCiro";
      break;
    default:
      pieTitle = "";
      pieMetricKey = "";
  }

  // İşçilik kartı için son yıl
  const lastIscilik =
    iscilikRows && iscilikRows.length > 0
      ? [...iscilikRows].sort((a, b) => (b.yil || 0) - (a.yil || 0))[0]
      : null;

  // Alış faturaları için son yıl
  const lastAlis =
    alisRows && alisRows.length > 0
      ? [...alisRows].sort((a, b) => (b.yil || 0) - (a.yil || 0))[0]
      : null;

  // Satış faturaları kartı için son yıl
  let currentSatisRow = null;
  if (satisRows && satisRows.length > 0) {
    currentSatisRow =
      [...satisRows].sort((a, b) => (b.yil || 0) - (a.yil || 0))[0];
  }

  const formatFxFromTl = (tl, rate, code) => {
    const n = Number(tl || 0);
    const r = Number(rate || 0);

    if (!n) return "-";
    if (!r) return "Kur yok";

    const val = n / r;
    return (
      val.toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) +
      " " +
      code
    );
  };

  /* ---- RENDER ---- */

  return (
    <div className="dashboard-root">
      {/* ÜST TOP BAR */}
   <header className="dash-topbar">
  <div className="dash-topbar-left">
    <div className="dash-topbar-logo">
      <img src={logo} alt="Logo" />
    </div>
  </div>

  <div className="dash-topbar-right">
    <div className="dash-topbar-user-name">
      {user?.fullName || "Alparslan ASKAR"}
    </div>
    <div className="dash-topbar-user-role">
      {user?.departman || "Genel Müdür"}
    </div>
    <button
  style={{
    marginLeft: "auto",
    fontSize: 12,
    fontWeight: 600,
    background: "transparent",
    border: "none",
    color: "#f97316",
    cursor: "pointer",
    textDecoration: "underline",
    padding: 0,
  }}
  onClick={() => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    window.location.reload();
  }}
>
  Log Out
</button>

  </div>
</header>



      {/* KPI SATIRI 1 */}
      <div className="dash-kpi-row">
        {/* Açık Siparişler */}
        <div className="dash-kpi-card">
          <div className="dash-kpi-title">Açık Siparişler</div>
          <div className="dash-kpi-value">
            {acikSiparisLoading
              ? "Yükleniyor..."
              : `${formatInt(acikSiparisAdet)} Adet`}
          </div>
          <div className="dash-kpi-trend" style={{ marginTop: 6 }}>
            {acikSiparisError ? (
              <span
                className="dash-kpi-trend-label"
                style={{ color: "#f97316" }}
              >
                Veri okunamadı: {acikSiparisError}
              </span>
            ) : (
              <span className="dash-kpi-trend-label">
                Toplam Değer:{" "}
                <strong>{formatCurrency(acikSiparisTutar)}</strong>
              </span>
            )}
          </div>
        </div>

        {/* Bekleyen Ürünler */}
        <div className="dash-kpi-card">
          <div className="dash-kpi-title">Bekleyen Ürünler</div>
          <div className="dash-kpi-value">
            {bekleyenLoading
              ? "Yükleniyor..."
              : `${formatInt(bekleyenAdet)} Adet`}
          </div>
          <div className="dash-kpi-trend" style={{ marginTop: 6 }}>
            {bekleyenError ? (
              <span
                className="dash-kpi-trend-label"
                style={{ color: "#f97316" }}
              >
                Veri okunamadı: {bekleyenError}
              </span>
            ) : (
              <span className="dash-kpi-trend-label">
                Toplam Değer:{" "}
                <strong>{formatCurrency(bekleyenTutar)}</strong>
              </span>
            )}
          </div>
        </div>

        {/* Stok Özeti */}
        <div className="dash-kpi-card">
          <div className="dash-kpi-title">Stok Özeti</div>
          <div className="dash-kpi-value">
            {stokLoading ? "Yükleniyor..." : formatKg(stokKg)}
          </div>
          <div className="dash-kpi-trend" style={{ marginTop: 6 }}>
            {stokError ? (
              <span
                className="dash-kpi-trend-label"
                style={{ color: "#f97316" }}
              >
                Veri okunamadı: {stokError}
              </span>
            ) : (
              <>
                <span
                  className="dash-kpi-trend-label"
                  style={{ display: "block" }}
                >
                  Toplam Değer:{" "}
                  <strong>{formatCurrency(stokTutar)}</strong>
                </span>
                <span
                  className="dash-kpi-trend-label"
                  style={{
                    display: "block",
                    fontSize: 11,
                    opacity: 0.8,
                    marginTop: 2,
                  }}
                >
                  ({formatKg(musteriMalzemeKg)} müşteri malzemesi stoktadır)
                </span>
              </>
            )}
          </div>
        </div>

        {/* Kalıp Stoğu */}
        <div className="dash-kpi-card">
          <div className="dash-kpi-title">Kalıp Stoğu (Şem Lastik)</div>
          <div className="dash-kpi-value">
            {kalipLoading ? "Yükleniyor..." : `${formatInt(kalipAdet)} Adet`}
          </div>
          <div className="dash-kpi-trend" style={{ marginTop: 6 }}>
            {kalipError ? (
              <span
                className="dash-kpi-trend-label"
                style={{ color: "#f97316" }}
              >
                Veri okunamadı: {kalipError}
              </span>
            ) : (
              <span className="dash-kpi-trend-label">
                Tahmini Değer:{" "}
                <strong>{formatEuro(kalipDegerEuro)}</strong>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* KPI SATIRI 2 */}
      <div className="dash-kpi-row" style={{ marginTop: 10 }}>
        {/* Satış Faturaları */}
        <div
          className="dash-kpi-card"
          style={{ cursor: "pointer" }}
          onClick={() => setSatisPanelOpen(true)}
        >
          <div className="dash-kpi-title">Satış Faturaları</div>
          <div className="dash-kpi-value" style={{ fontSize: 20 }}>
            {satisLoading
              ? "Yükleniyor..."
              : satisError
              ? "Hata"
              : currentSatisRow
              ? formatCurrency(currentSatisRow.genelToplam)
              : "Veri yok"}
          </div>
          <div className="dash-kpi-trend" style={{ marginTop: 6 }}>
            {satisError ? (
              <span
                className="dash-kpi-trend-label"
                style={{ color: "#f97316" }}
              >
                Veri okunamadı: {satisError}
              </span>
            ) : currentSatisRow ? (
              <>
                <span
                  className="dash-kpi-trend-label"
                  style={{ display: "block" }}
                >
                  Yıl: <strong>{currentSatisRow.yil}</strong> – Net:{" "}
                  <strong>{formatCurrency(currentSatisRow.netToplam)}</strong> –{" "}
                  KDV:{" "}
                  <strong>{formatCurrency(currentSatisRow.kdvToplami)}</strong>
                </span>
                <span
                  className="dash-kpi-trend-label"
                  style={{
                    display: "block",
                    fontSize: 11,
                    opacity: 0.8,
                    marginTop: 2,
                  }}
                >
                  Döviz Karş. ≈{" "}
                  <strong>
                    {formatFxFromTl(
                      currentSatisRow.genelToplam,
                      currentSatisRow.ortalamaEUR,
                      "EUR"
                    )}
                  </strong>{" "}
                  /{" "}
                  <strong>
                    {formatFxFromTl(
                      currentSatisRow.genelToplam,
                      currentSatisRow.ortalamaUSD,
                      "USD"
                    )}
                  </strong>
                </span>
              </>
            ) : (
              <span className="dash-kpi-trend-label">
                Yıllık satış faturası özetini görmek için tıklayın.
              </span>
            )}
          </div>
        </div>

        {/* Alış Faturaları */}
        <div
          className="dash-kpi-card"
          style={{ cursor: "pointer" }}
          onClick={() => setAlisPanelOpen(true)}
        >
          <div className="dash-kpi-title">Alış Faturaları</div>
          <div className="dash-kpi-value" style={{ fontSize: 20 }}>
            {alisLoading
              ? "Yükleniyor..."
              : alisError
              ? "Hata"
              : lastAlis
              ? formatCurrency(lastAlis.genelToplam)
              : "Veri yok"}
          </div>
          <div className="dash-kpi-trend" style={{ marginTop: 6 }}>
            {alisError ? (
              <span
                className="dash-kpi-trend-label"
                style={{ color: "#f97316" }}
              >
                Veri okunamadı: {alisError}
              </span>
            ) : lastAlis ? (
              <>
                <span
                  className="dash-kpi-trend-label"
                  style={{ display: "block" }}
                >
                  Yıl: <strong>{lastAlis.yil}</strong> – Net:{" "}
                  <strong>{formatCurrency(lastAlis.netToplam)}</strong> – KDV:{" "}
                  <strong>{formatCurrency(lastAlis.kdvToplami)}</strong>
                </span>
                <span
                  className="dash-kpi-trend-label"
                  style={{
                    display: "block",
                    fontSize: 11,
                    opacity: 0.8,
                    marginTop: 2,
                  }}
                >
                  Döviz Karş. ≈{" "}
                  <strong>
                    {formatFxFromTl(
                      lastAlis.genelToplam,
                      lastAlis.ortalamaEUR,
                      "EUR"
                    )}
                  </strong>{" "}
                  /{" "}
                  <strong>
                    {formatFxFromTl(
                      lastAlis.genelToplam,
                      lastAlis.ortalamaUSD,
                      "USD"
                    )}
                  </strong>
                </span>
              </>
            ) : (
              <span className="dash-kpi-trend-label">
                Detay tabloyu görmek için tıklayın.
              </span>
            )}
          </div>
        </div>

        {/* İşçilik Kar/Zarar Özeti */}
        <div
          className="dash-kpi-card"
          style={{ cursor: "pointer" }}
          onClick={() => setIscilikPanelOpen(true)}
        >
          <div className="dash-kpi-title">İşçilik Kar/Zarar Özeti</div>
          <div className="dash-kpi-value" style={{ fontSize: 20 }}>
            {iscilikLoading
              ? "Yükleniyor..."
              : iscilikError
              ? "Hata"
              : lastIscilik
              ? `${Number(lastIscilik.karZarar || 0).toLocaleString(
                  "tr-TR",
                  { maximumFractionDigits: 0 }
                )} TL`
              : "Veri yok"}
          </div>
          <div className="dash-kpi-trend" style={{ marginTop: 6 }}>
            {iscilikError ? (
              <span
                className="dash-kpi-trend-label"
                style={{ color: "#f97316" }}
              >
                Veri okunamadı: {iscilikError}
              </span>
            ) : lastIscilik ? (
              <span className="dash-kpi-trend-label">
                Yıl: <strong>{lastIscilik.yil}</strong> – Dövme Ciro:{" "}
                <strong>{formatCurrency(lastIscilik.dovmeCiroTL)}</strong>
              </span>
            ) : (
              <span className="dash-kpi-trend-label">
                Detay tabloyu görmek için tıklayın.
              </span>
            )}
          </div>
        </div>

        {/* Yıllık Rapor Detay */}
        <div
          className="dash-kpi-card"
          style={{ cursor: "pointer" }}
          onClick={() => setYearlyPanelOpen(true)}
        >
          <div className="dash-kpi-title">Yıllık Rapor Detay</div>
          <div className="dash-kpi-value" style={{ fontSize: 18 }}>
            Yıllık gider / satış tablosu
          </div>
          <div className="dash-kpi-trend" style={{ marginTop: 6 }}>
            <span className="dash-kpi-trend-label">
              Detay tabloyu görmek için tıklayın.
            </span>
          </div>
        </div>
      </div>

      {/* 4 BAR GRAFİK (Son 6 Ay) */}
      <div className="dash-grid-4">
        {/* Üretim Adedi */}
        <div
          className="dash-card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="dash-card-header">
            <h3 className="dash-card-title">Üretim Adedi (Son 6 Ay)</h3>
          </div>
          <div className="dash-chart-wrapper small">
            {adetLoading ? (
              <div>Veri yükleniyor...</div>
            ) : adetError ? (
              <div style={{ color: "tomato" }}>
                Veri okunamadı: {adetError}
              </div>
            ) : (
              <SimpleBarChart
                data={miniAdetData}
                valueKey="toplamAdet"
                valueFormat={null}
                lastCount={6}
                onBarClick={(item) => openDetail("adet", item)}
                showValue={false}
              />
            )}
          </div>
        </div>

        {/* Üretim KG */}
        <div
          className="dash-card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="dash-card-header">
            <h3 className="dash-card-title">Üretim KG (Son 6 Ay)</h3>
          </div>
          <div className="dash-chart-wrapper small">
            {kgLoading ? (
              <div>Veri yükleniyor...</div>
            ) : kgError ? (
              <div style={{ color: "tomato" }}>
                Veri okunamadı: {kgError}
              </div>
            ) : (
              <SimpleBarChart
                data={miniKgData}
                valueKey="toplamKg"
                valueFormat={null}
                lastCount={6}
                getBarClassName={() => "bar-kg"}
                onBarClick={(item) => openDetail("kg", item)}
                showValue={false}
              />
            )}
          </div>
        </div>

        {/* Dövme İşçilik Cirosu */}
        <div
          className="dash-card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="dash-card-header">
            <h3 className="dash-card-title">Dövme İşçilik Cirosu (6 Ay)</h3>
          </div>
          <div className="dash-chart-wrapper small">
            {dovmeCiroLoading ? (
              <div>Veri yükleniyor...</div>
            ) : dovmeCiroError ? (
              <div style={{ color: "tomato" }}>
                Veri okunamadı: {dovmeCiroError}
              </div>
            ) : (
              <SimpleBarChart
                data={miniDovmeCiroData}
                valueKey="toplamDovmeCiro"
                valueFormat={null}
                lastCount={6}
                getBarClassName={(item, ciroVal) => {
                  const giderVal = giderMapForMini[item.ay] ?? 0;
                  if (giderVal > ciroVal) return "bar-ciro-bad";
                  return "bar-ciro-ok";
                }}
                onBarClick={(item) => openDetail("dovmeCiro", item)}
                showValue={false}
              />
            )}
          </div>
        </div>

        {/* İşçilik Gideri */}
        <div
          className="dash-card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="dash-card-header">
            <h3 className="dash-card-title">İşçilik Gideri (6 Ay)</h3>
          </div>
          <div className="dash-chart-wrapper small">
            {giderLoading ? (
              <div>Veri yükleniyor...</div>
            ) : giderError ? (
              <div style={{ color: "tomato" }}>
                Veri okunamadı: {giderError}
              </div>
            ) : (
              <SimpleBarChart
                data={miniGiderData}
                valueKey="toplamGider"
                valueFormat={null}
                lastCount={6}
                getBarClassName={() => "bar-gider"}
                onBarClick={(item) => openDetail("gider", item)}
                showValue={false}
              />
            )}
          </div>
        </div>
      </div>

      {/* 4 PASTA GRAFİĞİ – Müşteri bazlı, mevcut yıl */}
      <div className="dash-grid-4" style={{ marginTop: 18 }}>
        {/* Adet Bazlı */}
        <div
          className="dash-card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="dash-card-header">
            <h3 className="dash-card-title">
              Müşteri Dağılımı – Adet ({currentYear})
            </h3>
          </div>
          <div className="dash-card-body">
            {customerYearLoading ? (
              <div>Veri yükleniyor...</div>
            ) : customerYearError ? (
              <div style={{ color: "tomato" }}>
                Veri okunamadı: {customerYearError}
              </div>
            ) : (
              <SimplePieChart
                data={pieAdetData}
                valueKey="value"
                labelKey="musteri"
                size={130}
                strokeWidth={18}
                showLegend={false}
                onSliceClick={() => openPieDetail("adet")}
              />
            )}
          </div>
        </div>

        {/* KG Bazlı */}
        <div
          className="dash-card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="dash-card-header">
            <h3 className="dash-card-title">
              Müşteri Dağılımı – KG ({currentYear})
            </h3>
          </div>
          <div className="dash-card-body">
            {customerYearLoading ? (
              <div>Veri yükleniyor...</div>
            ) : customerYearError ? (
              <div style={{ color: "tomato" }}>
                Veri okunamadı: {customerYearError}
              </div>
            ) : (
              <SimplePieChart
                data={pieKgData}
                valueKey="value"
                labelKey="musteri"
                size={130}
                strokeWidth={18}
                showLegend={false}
                onSliceClick={() => openPieDetail("kg")}
              />
            )}
          </div>
        </div>

        {/* Dövme İşçilik Cirosu Bazlı */}
        <div
          className="dash-card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="dash-card-header">
            <h3 className="dash-card-title">
              Müşteri Dağılımı – Dövme Ciro ({currentYear})
            </h3>
          </div>
          <div className="dash-card-body">
            {customerYearLoading ? (
              <div>Veri yükleniyor...</div>
            ) : customerYearError ? (
              <div style={{ color: "tomato" }}>
                Veri okunamadı: {customerYearError}
              </div>
            ) : (
              <SimplePieChart
                data={pieDovmeCiroData}
                valueKey="value"
                labelKey="musteri"
                size={130}
                strokeWidth={18}
                showLegend={false}
                onSliceClick={() => openPieDetail("dovmeCiro")}
              />
            )}
          </div>
        </div>

        {/* Toplam Ciro Bazlı */}
        <div
          className="dash-card"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div className="dash-card-header">
            <h3 className="dash-card-title">
              Müşteri Dağılımı – Toplam Ciro ({currentYear})
            </h3>
          </div>
          <div className="dash-card-body">
            {customerYearLoading ? (
              <div>Veri yükleniyor...</div>
            ) : customerYearError ? (
              <div style={{ color: "tomato" }}>
                Veri okunamadı: {customerYearError}
              </div>
            ) : (
              <SimplePieChart
                data={pieToplamCiroData}
                valueKey="value"
                labelKey="musteri"
                size={130}
                strokeWidth={18}
                showLegend={false}
                onSliceClick={() => openPieDetail("toplamCiro")}
              />
            )}
          </div>
        </div>
      </div>

      {/* BAR DETAY PANEL */}
      <DetailPanel
        open={detailOpen}
        onClose={closeDetail}
        title={detailTitle}
        data={detailData}
        valueKey={detailValueKey}
        valueFormat={detailFormat}
        years={detailYears}
        year={detailYear}
        onYearChange={setDetailYear}
        getBarClassName={detailBarClassName}
      />

      {/* PIE DETAY PANEL */}
      <PieDetailPanel
        open={pieOpen}
        onClose={closePieDetail}
        title={pieTitle}
        rawData={customerYearData}
        metricKey={pieMetricKey}
        years={pieYears}
        year={pieYear}
        onYearChange={setPieYear}
      />

      {/* YILLIK RAPOR DETAY PANELİ */}
      <YearlyReportPanel
        open={yearlyPanelOpen}
        onClose={() => setYearlyPanelOpen(false)}
        rows={yearlyRows}
        loading={yearlyLoading}
        error={yearlyError}
      />

      {/* AÇIK SİPARİŞLER PANELİ */}
      <OpenOrdersPanel
        open={openOrdersPanelOpen}
        onClose={() => setOpenOrdersPanelOpen(false)}
        rows={openOrdersRows}
        loading={openOrdersLoading}
        error={openOrdersError}
      />

      {/* ALIŞ FATURALARI PANELİ */}
      <AlisFaturaPanel
        open={alisPanelOpen}
        onClose={() => setAlisPanelOpen(false)}
        rows={alisRows}
        loading={alisLoading}
        error={alisError}
      />

      {/* SATIŞ FATURALARI PANELİ */}
      <SatisFaturaPanel
        open={satisPanelOpen}
        onClose={() => setSatisPanelOpen(false)}
        rows={satisRows}
        loading={satisLoading}
        error={satisError}
      />

      {/* İŞÇİLİK KAR/ZARAR PANELİ */}
      <IscilikKarPanel
        open={iscilikPanelOpen}
        onClose={() => setIscilikPanelOpen(false)}
        rows={iscilikRows}
        loading={iscilikLoading}
        error={iscilikError}
      />
    </div>
  );
}

/* ---------- APP WRAPPER: Login + Dashboard ---------- */

function App() {
  const [user, setUser] = useState(
    JSON.parse(localStorage.getItem("user")) || null
  );

  const handleLoginSuccess = (payload) => {
    localStorage.setItem("user", JSON.stringify(payload));
    localStorage.setItem("token", payload.token);
    setUser(payload);
  };

  // logout fonksiyonu istersen buradan da ekleriz
  // şimdilik sadece login kontrolü:
  if (!user) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

 // Kullanıcı varsa dashboard'u göster
return <HomeDashboard user={user} />;



}

export default App;
